<section class="flex h-full w-full flex-col overflow-hidden bg-white text-slate-900" role="dialog">
  <header class="flex shrink-0 items-center gap-3 border-b !border-slate-100 px-4 py-3">
    <div class="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-red-50 text-red-600">
      <app-icon>add_link</app-icon>
    </div>
    <div class="flex min-w-0 flex-1 flex-col">
      <h2 class="min-w-0 truncate !m-0 !text-base !font-bold !leading-6">{{ 'novaposhta.link.title' | translate }}</h2>
      <span class="truncate text-xs text-slate-400">{{ 'novaposhta.link.subtitle' | translate }}</span>
    </div>
    <app-button btnType="icon" [ariaLabel]="'close' | translate" (onChangeClick)="close.emit()">
      <app-icon>close</app-icon>
    </app-button>
  </header>

  @if (loading) {
    <div class="flex flex-1 flex-col gap-3 bg-slate-50 px-4 py-4">
      <div class="h-10 animate-pulse rounded-lg bg-slate-200/70"></div>
      <div class="h-20 animate-pulse rounded-lg bg-slate-200/70"></div>
      <div class="h-20 animate-pulse rounded-lg bg-slate-200/70"></div>
    </div>
  } @else {
    <div class="flex shrink-0 flex-col gap-3 border-b !border-slate-100 px-4 py-3">
      @if (cabinets.length > 1) {
        <app-select
          [formControl]="cabinetControl"
          [options]="cabinets"
          optionLabel="name"
          optionValue="id"
          [label]="'novaposhta.form.cabinet' | translate"
        />
      }
      <app-input
        [formControl]="searchControl"
        [label]="'novaposhta.link.search' | translate"
        [placeholder]="'novaposhta.link.search_placeholder' | translate"
        [showClear]="true"
      />
    </div>

    <div class="crm-scroll flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto bg-slate-50 px-4 py-3">
      @if (lookupNumber; as number) {
        <div class="flex items-center gap-3 rounded-lg border border-dashed !border-orange-300 bg-orange-50/60 px-3 py-2.5">
          <app-icon class="shrink-0 text-orange-500">travel_explore</app-icon>
          <span class="min-w-0 flex-1 text-sm text-slate-700">
            {{ 'novaposhta.link.lookup_text' | translate: { ttn: formatTtn(number) } }}
          </span>
          <app-button btnType="stroked" [customClass]="'!h-8 !rounded-lg !text-sm'" [disabled]="finding" (onChangeClick)="find()">
            {{ (finding ? 'novaposhta.link.finding' : 'novaposhta.link.find') | translate }}
          </app-button>
        </div>
      }
      @if (notFound) {
        <div class="flex items-center gap-2 rounded-lg bg-rose-50 px-3 py-2.5 text-sm text-rose-700">
          <app-icon class="shrink-0">search_off</app-icon>
          {{ 'novaposhta.errors.NP_SHIPMENT_NOT_FOUND' | translate }}
        </div>
      }
      @if (found) {
        <ng-container [ngTemplateOutlet]="row" [ngTemplateOutletContext]="{ $implicit: found }" />
      }

      <div class="flex items-center gap-2 pt-1">
        <span class="text-xs font-semibold uppercase tracking-wide text-slate-400">{{ 'novaposhta.link.recent' | translate }}</span>
        @if (searching) {
          <app-icon class="!h-3.5 !w-3.5 animate-spin !text-[14px] !leading-none text-slate-400">progress_activity</app-icon>
        }
      </div>

      @for (document of documents; track document.ttn_number) {
        <ng-container [ngTemplateOutlet]="row" [ngTemplateOutletContext]="{ $implicit: document }" />
      } @empty {
        @if (!searching) {
          <div class="flex flex-col items-center gap-2 rounded-lg border border-dashed !border-slate-300 bg-white px-6 py-8 text-center">
            <app-icon class="text-slate-300">inbox</app-icon>
            <span class="text-sm text-slate-500">{{ 'novaposhta.link.empty' | translate }}</span>
          </div>
        }
      }
    </div>

    <footer class="flex shrink-0 flex-col gap-3 border-t !border-slate-100 px-4 py-3">
      <p class="!m-0 flex items-center gap-1.5 text-xs text-slate-500">
        <app-icon class="!h-4 !w-4 shrink-0 !text-[16px] text-slate-400">sync_alt</app-icon>
        <span [innerHTML]="'novaposhta.link.hint' | translate"></span>
      </p>
      <div class="flex gap-2">
        <app-button btnType="stroked" class="flex-1" [customClass]="'w-full !h-10 !rounded-lg'" (onChangeClick)="close.emit()">
          {{ 'cancel' | translate }}
        </app-button>
        <app-button class="flex-1" [customClass]="'w-full !h-10 !rounded-lg'" [disabled]="!selected || saving" (onChangeClick)="save()">
          @if (selected && !saving) {
            {{ 'novaposhta.link.submit_ttn' | translate: { ttn: formatTtn(selected.ttn_number) } }}
          } @else {
            {{ (saving ? 'novaposhta.link.linking' : 'novaposhta.link.submit') | translate }}
          }
        </app-button>
      </div>
    </footer>
  }
</section>

<ng-template #row let-document>
  @let isSelected = selected?.ttn_number === document.ttn_number;
  @let selectable = isSelectable(document);
  <app-button
    btnType="no-btn"
    [customClass]="
      'flex gap-3 rounded-lg border bg-white px-3 py-2.5 transition ' +
      (isSelected
        ? '!border-orange-400 ring-2 ring-orange-100'
        : selectable
          ? '!border-slate-200 cursor-pointer hover:!border-slate-300'
          : '!border-slate-200 opacity-60 cursor-not-allowed')
    "
    (onChangeClick)="select(document)"
  >
    <app-icon class="mt-0.5 shrink-0" [ngClass]="isSelected ? 'text-orange-500' : 'text-slate-300'">
      {{ isSelected ? 'radio_button_checked' : 'radio_button_unchecked' }}
    </app-icon>
    <div class="flex min-w-0 flex-1 flex-col gap-1">
      <div class="flex flex-wrap items-center gap-2">
        <span class="font-mono text-sm font-bold tracking-wide text-slate-900">{{ formatTtn(document.ttn_number) }}</span>
        <span class="rounded-full px-2 py-0.5 text-[11px] font-semibold" [ngClass]="statusBadge(document)">
          {{ 'novaposhta.status.' + document.status | translate }}
        </span>
        <span class="ml-auto text-xs text-slate-400">{{ document.created_at | date: 'dd.MM.yyyy' }}</span>
      </div>
      <span class="truncate text-sm text-slate-700">
        <span class="font-medium">{{ document.recipient.name }}</span>
        <span class="text-slate-500"> · {{ formatNpPhone(document.recipient.phone) }} · {{ document.recipient.city_name }}</span>
      </span>
      <span class="truncate text-xs text-slate-500">
        {{ formatMoneyAmount(document.cargo.declared_cost, 'UAH') }}
        @if (document.cod.type !== 'none') {
          · {{ 'novaposhta.cod_type.' + document.cod.type | translate }} {{ formatMoneyAmount(document.cod.amount, 'UAH') }}
        }
      </span>
      @if (document.linked_card; as card) {
        <span class="flex items-center gap-1 text-xs font-medium text-amber-700">
          <app-icon class="!h-3.5 !w-3.5 !text-[14px] !leading-none">link</app-icon>
          @if (card.id === cardId) {
            {{ 'novaposhta.link.in_this_deal' | translate }}
          } @else if (card.name) {
            {{ 'novaposhta.link.in_deal' | translate: { name: card.name } }}
          } @else {
            {{ 'novaposhta.link.in_other_deal' | translate }}
          }
        </span>
      }
    </div>
  </app-button>
</ng-template>
